import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { parseDecimal } from "@/components/atoms/format";

export type Bucket = "1m" | "5m" | "1h" | "1d";
export type Range = "1h" | "1d" | "1w" | "1m" | "all";

export interface PricePoint {
  ts: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** Fetch & refresh candele OHLC dal backend.
 *
 * - polling ogni `refreshMs` (default 15s)
 * - quando il primo trade non è ancora arrivato, ritorna `[]` (NON dati sintetici) */
export function useOhlc(
  slug: string,
  bucket: Bucket = "5m",
  range: Range = "1d",
  refreshMs = 15_000,
): { points: PricePoint[]; loading: boolean } {
  const [points, setPoints] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await api.GET("/v1/markets/{slug}/ohlc", {
        params: {
          path: { slug },
          query: { bucket, range, limit: 500 },
        },
      });
      if (cancelled) return;
      if (data) {
        setPoints(
          data.points.map((p) => ({
            ts: new Date(p.bucket_ts),
            open: parseDecimal(p.open),
            high: parseDecimal(p.high),
            low: parseDecimal(p.low),
            close: parseDecimal(p.close),
            volume: parseDecimal(p.volume),
          })),
        );
      }
      setLoading(false);
    };
    load();
    const t = setInterval(load, refreshMs);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [slug, bucket, range, refreshMs]);

  return { points, loading };
}

/** Helper: estrai una serie di `close` per Sparkline. */
export function closes(points: PricePoint[]): number[] {
  return points.map((p) => p.close);
}
